<script setup lang="ts">
import EventCheckpointAutocomplete from '@/components/Filters/EventCheckpointAutocomplete.vue'

import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { ERdrChannel, RdrChannelKey } from '@/types/enum/RdrChannelEnum'
import {
    type CheckpointAssociateErrorModel,
    type CheckpointModel
} from '@/types/models/CheckpointModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import {
    ActionButton,
    BaseDialog,
    CreateButton,
    FullScreenLoader,
    StackLayout
} from '@rfc/rfc-components-library'
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import DeviceAvailableRdrChannels from '../components/DeviceAvailableRdrChannels.vue'

const store = useStore()

type Props = {
    data?: Partial<DeviceModel>
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const event = ref<EventModel | undefined>(props.data?.event)
const checkpoint = ref<CheckpointModel | undefined>(props.data?.checkpoint)

const submitted = ref(false)
const editMode = ref(false)
const loading = ref(false)

watch(
    () => props.data?.event,
    newEvent => (event.value = newEvent)
)

const selectedRdrChannel = ref<ERdrChannel | undefined>()
const associateError = ref<CheckpointAssociateErrorModel[]>()

const handleConfirm = (force?: boolean) => {
    submitted.value = true
    if (!props.data || !checkpoint.value?.id || !event.value?.id) return // TODO : Alerte
    loading.value = true
    store
        ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.ASSOCIATE_DEVICE}`, {
            device_id: props.data?.device_id ?? props.data?.id,
            checkpoint_id: checkpoint.value?.id,
            event_id: checkpoint.value?.event_id,
            config: selectedRdrChannel.value
                ? { [RdrChannelKey]: selectedRdrChannel.value }
                : undefined,
            force
        })
        .then(() => {
            emit('refresh')
            editMode.value = false
            submitted.value = false
            event.value = props.data?.event
            open.value = false
            checkpoint.value = undefined
        })
        .catch(err => (associateError.value = err))
        .finally(() => {
            if (force) associateError.value = undefined
            loading.value = false
        })
}

const handleSelectEvent = (value?: EventModel) => {
    checkpoint.value = undefined
    event.value = value
}

const open = ref(false)
</script>

<template>
    <FullScreenLoader :loading="loading" />
    <ActionButton
        @click="open = true"
        :label="$t('device.actions.reassignCheckpoint')"
        v-bind="$attrs" />
    <BaseDialog
        :title="$t('device.actions.reassignCheckpoint')"
        :open="open"
        @confirm="handleConfirm()"
        @close="open = false"
        :disabled="!checkpoint"
        class="event-checkpoint-component modal-selector-half-width">
        <template #actions>
            <CreateButton
                v-if="associateError"
                :label="$t('checkpoint.buttons.forceAssociate')"
                @click="handleConfirm(true)" />
        </template>
        <template #content>
            <StackLayout :gap="8" isResponsive>
                <EventCheckpointAutocomplete
                    :event="event"
                    :checkpoint="checkpoint"
                    @selectEvent="handleSelectEvent"
                    @selectCheckpoint="checkpoint = $event" />
            </StackLayout>
            <DeviceAvailableRdrChannels
                :device="data"
                :associates="checkpoint?.associates"
                :associateError="associateError"
                :selectedRdrChannel="selectedRdrChannel"
                @selectChannel="selectedRdrChannel = $event" />
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.event-checkpoint-component {
    & :deep(.dropdown-content) {
        position: fixed;
        display: flex;
        flex-flow: wrap;
    }
}
</style>
